'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useStore } from '@/lib/store';
import { useAuth } from '@/contexts/AuthContext';
import { getRecommendationEngine, ContentItem } from '@/lib/recommendation-engine';
import {
  Sparkles,
  TrendingUp,
  Languages,
  Award,
  ChevronLeft,
  ChevronRight,
  Play,
  Clock,
  Film,
  Tv,
  ArrowRight,
} from 'lucide-react';
import { MediaCard } from '@/components/MediaCard';

interface PersonalizedRowProps {
  title: string;
  category: 'trending' | 'similar' | 'genre' | 'mixed' | 'continue' | 'watch_again' | 'language_specific' | 'network' | 'collaborative' | 'keywords' | 'cast_crew' | 'personalized';
  currentContent?: ContentItem;
  language?: string;
  icon?: React.ReactNode;
  description?: string;
  className?: string;
}

export function PersonalizedRow({
  title,
  category,
  currentContent,
  language,
  icon,
  description,
  className = '',
}: PersonalizedRowProps) {
  const { user } = useAuth();
  const router = useRouter();
  const { watchlist, dislikedItems, addToWatchlist } = useStore();
  const [recommendations, setRecommendations] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [scrollPosition, setScrollPosition] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const prevWatchlistLengthRef = useRef(watchlist.length);
  const hasLoadedRef = useRef(false);

  // Generate a unique ID for this row instance to prevent key collisions across different rows
  const rowId = useRef(`${category}-${Math.random().toString(36).substr(2, 9)}`).current;

  useEffect(() => {
    if (!user) return;

    // Always load on initial mount
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadRecommendations();
      return;
    }

    // After initial load, only reload if watchlist length changed by more than 1 item
    // This prevents reloading when just adding a single item
    const watchlistDiff = Math.abs(watchlist.length - prevWatchlistLengthRef.current);
    if (watchlistDiff > 1) {
      prevWatchlistLengthRef.current = watchlist.length;
      loadRecommendations();
    }
  }, [user, watchlist.length, dislikedItems, category, currentContent, language]);

  const loadRecommendations = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const engine = getRecommendationEngine();
      await engine.initialize(user.uid, watchlist, dislikedItems);

      let recs: ContentItem[] = [];

      switch (category) {
        case 'continue':
          recs = await engine.getContinueWatching(user.uid);
          break;
        case 'watch_again':
          recs = await engine.getWatchAgain(user.uid, watchlist);
          break;
        case 'language_specific':
          if (language) {
            recs = await engine.getLanguageSpecificContent(language);
          }
          break;
        case 'personalized':
          recs = await engine.getPersonalizedRecommendations(currentContent, category, watchlist);
          break;
        default:
          recs = await engine.getPersonalizedRecommendations(currentContent, category);
          break;
      }

      // Deduplicate recommendations before setting state
      const uniqueRecs = recs.filter((item, index, self) =>
        index === self.findIndex(t => t.id === item.id && t.type === item.type)
      );

      setRecommendations(uniqueRecs.slice(0, 20));
    } catch (error) {
      // Error loading recommendations
    } finally {
      setLoading(false);
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const itemWidth = 300; // Approximate width of each item
    const scrollAmount = itemWidth * 3; // Scroll 3 items at a time
    const newPosition = direction === 'left'
      ? Math.max(0, scrollPosition - scrollAmount)
      : Math.min(
          container.scrollWidth - container.clientWidth,
          scrollPosition + scrollAmount
        );

    container.scrollTo({
      left: newPosition,
      behavior: 'smooth',
    });
    setScrollPosition(newPosition);
  };

  const getIcon = () => {
    if (icon) return icon;

    switch (category) {
      case 'trending':
        return <TrendingUp className="h-6 w-6 text-red-500" />;
      case 'continue':
        return <Play className="h-6 w-6 text-red-500" />;
      case 'watch_again':
        return <Clock className="h-6 w-6 text-red-500" />;
      case 'language_specific':
        return <Languages className="h-6 w-6 text-red-500" />;
      case 'genre':
        return <Award className="h-6 w-6 text-red-500" />;
      default:
        return <Sparkles className="h-6 w-6 text-red-500" />;
    }
  };

  if (!user || (loading && recommendations.length === 0)) {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="flex items-center gap-3">
          {getIcon()}
          <div className="space-y-1">
            <Skeleton className="h-6 w-48" />
            {description && <Skeleton className="h-4 w-32" />}
          </div>
        </div>
        <div className="flex gap-4 overflow-hidden">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="w-64 aspect-[2/3] flex-shrink-0" />
          ))}
        </div>
      </div>
    );
  }

  if (recommendations.length === 0) {
    return null;
  }

  const canScrollLeft = scrollPosition > 0;
  const canScrollRight = scrollContainerRef.current
    ? scrollPosition < scrollContainerRef.current.scrollWidth - scrollContainerRef.current.clientWidth
    : false;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header - matches default MediaSection style */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {getIcon()}
          <h2 className="text-2xl font-bold text-white">{title}</h2>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-red-400 hover:text-red-300"
          onClick={() => {
            // Navigate to browse page with category filter
            const categoryMap: Record<string, string> = {
              'continue': 'trending',
              'watch_again': 'popular-shows',
              'language_specific': 'popular-movies',
              'trending': 'trending',
            };
            router.push(`/browse/${categoryMap[category] || 'trending'}`);
          }}
        >
          View All
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>

      {/* Content - matches default MediaSection style */}
      <div className="overflow-x-auto">
        <div
          ref={scrollContainerRef}
          className="flex gap-4 md:gap-6 pb-6"
          style={{ width: 'max-content' }}
          onScroll={(e) => setScrollPosition(e.currentTarget.scrollLeft)}
        >
          {recommendations
            .filter(item => item.poster_path && item.poster_path.trim() !== '')
            .map((item, index) => (
              <MediaCard
                key={`${rowId}-${item.type}-${item.id}-${index}`}
                item={item}
                showAddButton={true}
                size="medium"
              />
            ))}
        </div>
      </div>
    </div>
  );
}